import {
    faExclamationTriangle,
    faHome,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import capmoLogo from "../../../assets/images/capmo_logo.svg";
import { AgGridAtom, ModalAtom } from "../../../components/atoms/atom";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import {
    downloadTicketCategoryPdf,
    useCreateTicketCategoryMutation,
    useDeleteTicketCategoryMutation,
    useGetProjectTicketCategoriesQuery,
    useUpdateTicketCategoryMutation,
} from "../../../store/services/ticketCategoriesService";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import {
    formatValueToLocale,
    getFormattedDate,
    showToast,
} from "../../../utils/method";
import { searchData } from "../../../utils/search";
import AddCategoryModal from "./add-category-modal";
import "./project-ticket-categories.scss";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";

const getCategoryColumns = (t, onEdit, onDelete, onDownloadPdf) => [
    {
        field: "createdAt",
        headerName: t("projects.provided"),
        headerTooltip: t("projects.provided"),
        unSortIcon: true,
        valueFormatter: (params) => getFormattedDate(params.value),
        maxWidth: 160,
        minWidth: 160,
    },
    {
        field: "isFromCapmo",
        headerName: t("common.source"),
        headerTooltip: t("common.source"),
        maxWidth: 60,
        cellRenderer: (params) => {
            const { isFromCapmo, capmoChangesRejected, isLocal } = params.data;

            if (!isFromCapmo && !isLocal) {
                return null;
            }

            if (isFromCapmo) {
                return (
                    <div className="capmo-logo-wrapper">
                        <img
                            src={capmoLogo}
                            alt="CAPMO"
                            height="20"
                            width="20"
                            title={
                                capmoChangesRejected
                                    ? t(`lv.${capmoChangesRejected}`)
                                    : t("lv.from-capmo")
                            }
                            className="d-inline-block capmo-logo"
                        />
                        {capmoChangesRejected && (
                            <span
                                className={`capmo-status-icon ${capmoChangesRejected ===
                                    "CAPMO_CATEGORY_DELETED"
                                    ? "status-icon-deleted"
                                    : "status-icon-error"
                                    }`}
                                title={t(`lv.${capmoChangesRejected}`)}
                            >
                                {capmoChangesRejected ===
                                    "CAPMO_CATEGORY_DELETED" ? (
                                    <FontAwesomeIcon icon={faTrash} />
                                ) : (
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                )}
                            </span>
                        )}
                    </div>
                );
            }

            if (isLocal) {
                return (
                    <FontAwesomeIcon
                        icon={faHome}
                        title={t("projects.local-project")}
                        className="ptc-home-icon"
                    />
                );
            }

            return null;
        },
    },
    {
        field: "name",
        headerName: t("common.name"),
        headerTooltip: t("common.name"),
        unSortIcon: true,
        flex: 1,
        minWidth: 100,
    },
    {
        field: "companyName",
        headerName: t("ticket-category.supplier"),
        headerTooltip: t("ticket-category.supplier"),
        unSortIcon: true,
        flex: 1,
    },
    {
        field: "vat",
        headerName: t("projects.settings.vat"),
        headerTooltip: t("projects.settings.vat"),
        unSortIcon: true,
        type: "numericColumn",
        valueFormatter: (params) => {
            const value = params.value;
            const hasDecimals = value % 1 !== 0;
            return hasDecimals
                ? `${formatValueToLocale(value)} %`
                : `${Math.round(value)} %`;
        },
        maxWidth: 100,
        minWidth: 100,
    },
    {
        field: "discount",
        headerName: t("projects.settings.discount"),
        headerTooltip: t("projects.settings.discount"),
        unSortIcon: true,
        type: "numericColumn",
        valueFormatter: (params) => {
            const value = params.value;
            // Check if value has decimals
            const hasDecimals = value % 1 !== 0;
            return hasDecimals
                ? `${formatValueToLocale(value)} %`
                : `${Math.round(value)} %`;
        },
        maxWidth: 130,
        minWidth: 130,
    },
    {
        field: "cashDiscount",
        headerName: t("projects.settings.cash-discount"),
        headerTooltip: t("projects.settings.cash-discount"),
        unSortIcon: true,
        type: "numericColumn",
        maxWidth: 150,
        minWidth: 150,
        cellRenderer: (params) => {
            const { cashDiscount, cashDiscountDays } = params.data;
            const hasDecimals = cashDiscount % 1 !== 0;
            const formattedValue = hasDecimals ? formatValueToLocale(cashDiscount) : Math.round(cashDiscount);
            return (
                <div className="ptc-cash-cell">
                    <span className="ptc-cash-cell__value">{formattedValue}%</span>
                    <span className="ptc-cash-cell__days">{cashDiscountDays} {t("projects.settings.days")}</span>
                </div>
            );
        },
    },
    {
        field: "securityRetention",
        headerName: t("ticket-category.contract-performance-security"),
        headerTooltip: t("ticket-category.contract-performance-security"),
        unSortIcon: true,
        type: "numericColumn",
        cellRenderer: (params) => {
            const {
                contractPerformanceSecurityPercent,
                contractPerformanceSecurityWithOrWithoutNachtraege,
                contractPerformanceSecurityCalculatedOrFix,
            } = params.data;
            return (
                <div className="ptc-security-cell">
                    <span className="ptc-security-cell__pct">{contractPerformanceSecurityPercent}%</span>
                    <div className="ptc-security-cell__tags">
                        <span className="ptc-tag">{t(`ticket-category.with-nachtraege-option.${contractPerformanceSecurityWithOrWithoutNachtraege}`)}</span>
                        <span className="ptc-tag">{t(`ticket-category.calculated-or-fix-option.${contractPerformanceSecurityCalculatedOrFix}`)}</span>
                    </div>
                </div>
            );
        },
    },
    {
        field: "contractualWarrantyRetention",
        headerName: t("ticket-category.warranty-security"),
        headerTooltip: t("ticket-category.warranty-security"),
        unSortIcon: true,
        type: "numericColumn",
        cellRenderer: (params) => {
            const {
                warrantySecurityPercent,
                warrantySecurityWithOrWithoutNachtraege,
                warrantySecurityCalculatedOrFix,
            } = params.data;
            return (
                <div className="ptc-security-cell">
                    <span className="ptc-security-cell__pct">{warrantySecurityPercent}%</span>
                    <div className="ptc-security-cell__tags">
                        <span className="ptc-tag">{t(`ticket-category.with-nachtraege-option.${warrantySecurityWithOrWithoutNachtraege}`)}</span>
                        <span className="ptc-tag">{t(`ticket-category.calculated-or-fix-option.${warrantySecurityCalculatedOrFix}`)}</span>
                    </div>
                </div>
            );
        },
    },
    createActionColumn({
        t,
        actions: [
            {
                projectPermission: {
                    area: PROJECT_AREA.TICKET_CATEGORIES,
                    action: PROJECT_ACTION.DOWNLOAD,
                },
                onClick: onDownloadPdf,
                label: "common.download-pdf",
            },
            {
                projectPermission: { area: PROJECT_AREA.TICKET_CATEGORIES, action: PROJECT_ACTION.EDIT },
                onClick: onEdit,
                label: "help.action-edit",
            },
            {
                projectPermission: { area: PROJECT_AREA.TICKET_CATEGORIES, action: PROJECT_ACTION.DELETE },
                onClick: onDelete,
                label: "help.action-delete",
                danger: true,
            },
        ]
    }),
];

function ProjectTicketCategories() {
    const { t, i18n } = useTranslation();

    const projectDetails = useSelector(selectProjectDetails);
    const { id: projectId } = projectDetails || {};

    const {
        data: categoriesResponse,
        isLoading,
    } = useGetProjectTicketCategoriesQuery({ projectId }, { skip: !projectId });
    const categories = useMemo(
        () => categoriesResponse?.items ?? [],
        [categoriesResponse],
    );

    const [createTicketCategory] = useCreateTicketCategoryMutation();
    const [updateTicketCategory] = useUpdateTicketCategoryMutation();
    const [deleteTicketCategory] = useDeleteTicketCategoryMutation();

    const {
        can,
        isLoading: permissionLoading,
        filterByCategoryScope,
        ticketCategoryIds,
    } = useProjectPermission();
    const canViewCategories =
        !permissionLoading && can(PROJECT_AREA.TICKET_CATEGORIES, PROJECT_ACTION.VIEW);
    const hasCategoryAccess =
        ticketCategoryIds === null || ticketCategoryIds.length > 0;
    const canCreateCategory = can(PROJECT_AREA.TICKET_CATEGORIES, PROJECT_ACTION.CREATE);

    const [filteredCategories, setFilteredCategories] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [filterItems, setFilterItems] = useState(
        getSearchAddFilter({ addTextKey: "projects.add-category" }),
    );

    // Update filtered categories when categories change
    useEffect(() => {
        setFilteredCategories(
            filterByCategoryScope(categories || [], (item) => item.id),
        );
    }, [categories, filterByCategoryScope]);

    const handleEdit = (data) => {
        setCurrentCategory(data);
        setShowAddModal(true);
    };

    const handleDelete = (data) => {
        setSelectedCategory(data);
        setShowDeleteModal(true);
    };

    const handleDownloadPdf = async (data) => {
        if (!data?.id) return;
        try {
            await downloadTicketCategoryPdf(
                data.id,
                {
                    lang: i18n.language?.startsWith("en") ? "en" : "de",
                    categoryName: data.name || "",
                },
                projectId,
            );
        } catch (error) {
            showToast(t("errors.general"), { type: "error" });
        }
    };

    const onDeleteCategory = async () => {
        try {
            await deleteTicketCategory({
                projectId,
                categoryId: selectedCategory.id,
            }).unwrap();
            showToast(t("projects.category-deleted"), { type: "success" });
            setShowDeleteModal(false);
        } catch (error) {
            showToast(error?.data?.message || error?.message || t("errors.general"), { type: "error" });
        }
    };

    const handleAddCategory = async (formData) => {
        try {
            if (currentCategory) {
                await updateTicketCategory({
                    projectId,
                    categoryId: currentCategory.id,
                    ...formData,
                }).unwrap();
                showToast(t("projects.category-updated-success"), {
                    type: "success",
                });
            } else {
                await createTicketCategory({
                    projectId,
                    ...formData,
                }).unwrap();
                showToast(t("projects.category-added"), { type: "success" });
            }
            setShowAddModal(false);
            setCurrentCategory(null);
        } catch (error) {
            showToast(error?.data?.message || error?.message || t("errors.general"), { type: "error" });
        }
    };

    const categoryColumns = getCategoryColumns(
        t,
        handleEdit,
        handleDelete,
        handleDownloadPdf,
    );

    const onFilterAction = (index, key, info) => {
        const localFilterItems = [...filterItems];
        localFilterItems[index].value = info?.value;
        setFilterItems(localFilterItems);

        let filtered = [...categories];

        // Apply all active filters
        localFilterItems.forEach((item) => {
            if (item.key === "search" && item.value) {
                filtered = searchData(item.value, filtered, categoryColumns);
            }
        });

        setFilteredCategories(filtered);

        // Handle add action separately
        if (key === "add") {
            setCurrentCategory(null);
            setShowAddModal(true);
        }
    };

    if (permissionLoading) {
        return (
            <div className="project-ticket-categories-page">
                <ProjectHeader
                    projectName={projectDetails?.name}
                    tabTitle={t("projects.ticket-categories")}
                />
                <div className="page-container">
                    <TableSkeleton />
                </div>
            </div>
        );
    }

    if (!canViewCategories || !hasCategoryAccess) {
        return (
            <div className="project-ticket-categories-page">
                <ProjectHeader
                    projectName={projectDetails?.name}
                    tabTitle={t("projects.ticket-categories")}
                />
                <NoPermissionAlert />
            </div>
        );
    }

    return (
        <div className="project-ticket-categories-page">
            <ProjectHeader
                projectName={projectDetails?.name}
                tabTitle={t("projects.ticket-categories")}
            />
            <div className="page-container">
                <FilterBar
                    items={filterItems}
                    onFilterAction={onFilterAction}
                    canAdd={canCreateCategory}
                />

                <div className="flex-grow-1 ag-grid-parent">
                    {isLoading && filteredCategories.length === 0 ? (
                        <TableSkeleton />
                    ) : (
                        <AgGridAtom
                            columnDefs={categoryColumns}
                            rowData={filteredCategories}
                            context={{ projectCan: can }}
                            // Use the correct AgGrid props
                            overlayLoadingTemplate=""
                            overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("projects.no-ticket-categories")}</span>`}
                            // Use this for loading state
                            defaultColDef={{
                                flex: 1,
                                sortable: true,
                                wrapText: false,
                                autoHeight: false,
                            }}
                            rowHeight={64}
                        />
                    )}
                </div>

                {/* Add/Edit Category Modal */}
                <AddCategoryModal
                    show={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        setCurrentCategory(null);
                    }}
                    onSubmit={handleAddCategory}
                    categoryData={currentCategory}
                />

                {/* Delete Confirmation Modal */}
                <ModalAtom
                    title={t("projects.delete-category")}
                    body={<p>{t("projects.confirm-delete-category")}</p>}
                    saveText="delete"
                    show={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    handleSubmit={onDeleteCategory}
                />
            </div>
        </div>
    );
}

export default ProjectTicketCategories;
